import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from './base.service';
import { environment } from '../../../environments';

export interface CreateDocumentDto {
  documentType: string;
  contractId?: string;
  structureId?: string;
  structureName?: string;
  professionalId?: string;
  professionalName?: string;
  file: File;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentService extends BaseService {
  uploadApiUrl = environment.uploadApiUrl;

  constructor(http: HttpClient) {
    super(http);
  }

  private createFormData(documentData: CreateDocumentDto): FormData {
    const formData = new FormData();
    formData.append('documentType', documentData.documentType);
    if (documentData.contractId) {
      formData.append('contractId', documentData.contractId);
    }
    if (documentData.structureId) {
      formData.append('structureId', documentData.structureId);
    }
    if (documentData.structureName) {
      formData.append('structureName', documentData.structureName);
    }
    if (documentData.professionalId) {
      formData.append('professionalId', documentData.professionalId);
    }
    if (documentData.professionalName) {
      formData.append('professionalName', documentData.professionalName);
    }
    if (documentData.file) {
      formData.append('file', documentData.file, documentData.file.name);
    }
    return formData;
  }

  // Uploader un document
  uploadDocument(
    passportNumber: string,
    documentData: CreateDocumentDto,
    reportProgress: boolean = false
  ): Observable<any> {
    const formData = this.createFormData(documentData);

    // On ne met pas le Content-Type car Angular le gère automatiquement pour FormData
    if (reportProgress) {
      return this.http.post(
        `${this.uploadApiUrl}/${passportNumber}`,
        formData,
        {
          reportProgress: true,
          observe: 'events'
        }
      );
    } else {
      return this.http.post(
        `${this.uploadApiUrl}/${passportNumber}`,
        formData
      );
    }
  }
  // Obtenir tous les documents
  getDocuments(): Observable<any> {
    return this.http.get(`${this.baseUrl}/documents`, {
      headers: this.getHeaders(),
    });
  }

  // Obtenir les documents par numéro de passeport
  getDocumentsByPassportNumber(
    passportNumber: string,
    contractId?: string | null,
    paginationParams?: { page?: number; limit?: number; favoritesOnly?: boolean }
  ): Observable<any> {
    let params = new HttpParams();
    if (contractId) {
      params = params.set('contractId', contractId);
    }
    if (paginationParams?.page) {
      params = params.set('page', paginationParams.page.toString());
    }
    if (paginationParams?.limit) {
      params = params.set('limit', paginationParams.limit.toString());
    }
    if (paginationParams?.favoritesOnly !== undefined) {
      params = params.set('favoritesOnly', paginationParams.favoritesOnly.toString());
    }

    return this.http.get(`${this.baseUrl}/documents/${passportNumber}`, {
      headers: this.getHeaders(),
      params,
    });
  }

  // Obtenir les détails d'un document
  getDocumentDetails(
    passportNumber: string,
    documentId: string,
    includeBase64?: boolean
  ): Observable<any> {
    let params = new HttpParams();
    if (includeBase64 !== undefined) {
      params = params.set('includeBase64', includeBase64.toString());
    }
    return this.http.get(
      `${this.baseUrl}/documents/${passportNumber}/${documentId}`,
      {
        headers: this.getHeaders(),
        params,
      }
    );
  }

  // Obtenir une URL presignée pour un document
  getPresignedUrl(
    url: string,
    expiryHours?: number
  ): Observable<{ url: string }> {
    let params = new HttpParams().set('url', url);
    if (expiryHours !== undefined) {
      params = params.set('expiryHours', expiryHours.toString());
    }
    return this.http.get<{ url: string }>(
      `${this.baseUrl}/documents/presigned-url`,
      {
        headers: this.getHeaders(),
        params,
      }
    );
  }

  // Marquer/démarquer un document comme favori
  toggleFavorite(documentId: string, isFavorite: boolean): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/documents/toggle-favorite`,
      {
        documentId,
        isFavorite
      },
      {
        headers: this.getHeaders(),
      }
    );
  }

  // Obtenir les documents d'une prestation
  getPrestationDocuments(prestationId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/prestations/${prestationId}/documents`,
      {
        headers: this.getHeaders(),
      }
    );
  }
}
